import React, { useState, useMemo } from 'react';
import {
  ScanContainer,
  ScanHeaderRow,
  ScanLabel,
  ScanButton,
  ProductDropdown,
  DropdownContainer,
  ViewReportButton,
} from './styled';
import {
  useCreateEncephalonScanMutation,
  useGetEncephaloneStudyDataQuery,
  useGetEncephalonScanQuery,
} from '../../services/dicomService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEncephalon } from '../../contexts/EncephalonContext';
import { config } from 'services/config';

interface ProductType {
  value: string;
  label: string;
}

const PRODUCT_TYPES: ProductType[] = [
  { value: 'ECHOMEASURE', label: 'ECHOMEASURE' },
  { value: 'ECHOMEASURE-V2', label: 'ECHOMEASURE-V2' },
  { value: 'CARDIOVISION', label: 'CARDIOVISION' },
  { value: 'CARDIOVISION-V2', label: 'CARDIOVISION-V2' },
  { value: 'ECHOGPT', label: 'ECHOGPT' },
];

const ScanSection: React.FC<{ studyReady: boolean; name: string }> = ({ studyReady, name }) => {
  const [selectedProduct, setSelectedProduct] = useState<string>(PRODUCT_TYPES[0].value);
  const { triggerScanUpdate } = useEncephalon();

  const { data: studyData } = useGetEncephaloneStudyDataQuery(name, { skip: !name });
  const studyUuid = studyData?.results?.[0]?.uuid;

  const { data: scanData } = useGetEncephalonScanQuery(studyUuid, {
    skip: !studyUuid,
    pollingInterval: 10000,
  });
  const latestScans = scanData?.results || [];

  const scanForSelectedProduct = useMemo(() => {
    return latestScans.find(
      (s: { product: string }) => s.product?.toLowerCase() === selectedProduct.toLowerCase()
    );
  }, [latestScans, selectedProduct]);
  const scanStatus = scanForSelectedProduct?.status?.toUpperCase() || '';
  const [createScan, { isLoading }] = useCreateEncephalonScanMutation();

  const productStatuses = useMemo(() => {
    const statusMap: Record<string, string> = {};
    PRODUCT_TYPES.forEach(product => {
      const scan = latestScans.find(
        (s: { product: string }) => s.product?.toLowerCase() === product.value.toLowerCase()
      );
      statusMap[product.value] = scan?.status?.toUpperCase() || '';
    });
    return statusMap;
  }, [latestScans]);

  const anyProductPending = useMemo(() => {
    return Object.values(productStatuses).some(
      status => status === 'PENDING' || status === 'STARTED'
    );
  }, [productStatuses]);
  const finalButtonDisabled =
    !studyReady ||
    !studyUuid ||
    scanStatus === 'COMPLETED' ||
    scanStatus === 'PENDING' ||
    scanStatus === 'STARTED' ||
    (scanStatus === 'FAILED' && anyProductPending) ||
    (scanStatus === '' && anyProductPending);

  const handleCreateScan = async () => {
    if (!studyUuid || finalButtonDisabled) {
      toast.error('Study is not ready or scan already exists.');
      return;
    }

    try {
      await createScan({
        study_uuid: studyUuid,
        product: selectedProduct,
      }).unwrap();

      triggerScanUpdate();
      toast.success(`Scan created successfully for ${selectedProduct}!`);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err: unknown) {
      toast.error('Failed to create scan.');
    }
  };

  const handleViewReport = () => {
    if (!scanForSelectedProduct?.report) {
      toast.error('No report available for this scan');
      return;
    }

    let url = `${config.ENCEPHALON_FRONTEND_URL}report/${scanForSelectedProduct.report}`;
    if (scanForSelectedProduct.product === 'ECHOGPT') {
      url = `${config.ENCEPHALON_FRONTEND_URL}echogpt/${scanForSelectedProduct.report}`;
    }
    window.open(url, '_blank');
  };

  const getButtonText = (): string => {
    if (isLoading) return 'CREATING…';
    if (!studyReady || !studyUuid) return 'STUDY REQUIRED';
    if (scanStatus === 'PENDING') return 'SCAN PENDING';
    if (scanStatus === 'STARTED') return 'SCAN STARTED';
    if (scanStatus === 'COMPLETED') return 'SCAN EXISTS';
    if (scanStatus === 'FAILED') return 'RETRY';
    return 'CREATE SCAN';
  };

  const getProductStatusLabel = (productValue: string) => {
    return productStatuses[productValue] ? ` (${productStatuses[productValue]})` : '';
  };

  return (
    <ScanContainer>
      <ScanHeaderRow>
        <ScanLabel>Create Scan</ScanLabel>
      </ScanHeaderRow>

      <DropdownContainer>
        <ProductDropdown
          value={selectedProduct}
          onChange={e => setSelectedProduct(e.target.value)}
          disabled={!studyReady || !studyUuid}
        >
          {PRODUCT_TYPES.map(prod => (
            <option
              key={prod.value}
              value={prod.value}
              title={prod.label + getProductStatusLabel(prod.value)}
            >
              {prod.label}
              {getProductStatusLabel(prod.value)}
            </option>
          ))}
        </ProductDropdown>
        <div style={{ display: 'flex', gap: '10px' }}>
          {scanStatus === 'COMPLETED' && (
            <ViewReportButton onClick={handleViewReport}>VIEW SCAN REPORT</ViewReportButton>
          )}
          <ScanButton
            onClick={handleCreateScan}
            disabled={finalButtonDisabled}
            data-disabled={finalButtonDisabled}
          >
            {getButtonText()}
          </ScanButton>
        </div>
      </DropdownContainer>
    </ScanContainer>
  );
};

export default ScanSection;
